import { Grid3X3 } from 'lucide-react'
import { useEffect, useRef, useState, type ComponentType } from 'react'
import { Link, useParams } from 'react-router'
import { FormError } from '@/components/FormError'
import { Loader, PageLoader } from '@/components/Loader'
import { page } from '@/components/layout'
import { useTabSwipe } from '@/components/modern/useTabSwipe'
import { BarRow } from '@/components/nav/BarRow'
import { MomentsIcon, PhotosIcon, RecipesIcon, type PlaceIconProps } from '@/components/nav/icons'
import { AskDialog } from '@/components/people/Connect'
import { ConnectionOptions } from '@/components/people/PersonProfile'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import {
  bothKnow,
  feedLink,
  useAccount,
  useAccountItems,
  useAsk,
  type AccountProfile,
  type AccountSection,
  type FeedItem,
} from '@/lib/connections'
import { usePageFrame } from '@/lib/frame'
import { refUrl } from '@/lib/photos'
import { useIsPhone } from '@/lib/platform'
import { cn } from '@/lib/utils'

type Tab = 'all' | AccountSection
const TABS: { key: Tab; label: string; icon: ComponentType<PlaceIconProps> }[] = [
  { key: 'all', label: 'Everything', icon: ({ className }) => <Grid3X3 className={className} strokeWidth={1.75} /> },
  { key: 'photos', label: 'Photos', icon: PhotosIcon },
  { key: 'moments', label: 'Moments', icon: MomentsIcon },
  { key: 'recipes', label: 'Recipes', icon: RecipesIcon },
]

// Someone's own page, opened from their profile or from Search, like a profile on Instagram: their face and name,
// what they are to you, then tabs for what they've added that you can see (everything, then each section they share
// in), as a grid. On a phone their name is in the top bar and the tabs stay pinned under it as you scroll. Back goes
// to wherever you came from.
export function AccountPage() {
  const { id = '' } = useParams()
  const account = useAccount(id)
  usePageFrame(undefined, { title: account.data?.name ?? 'Profile', back: { to: '/people', label: 'Back' } })
  if (account.isPending) return <PageLoader />
  if (!account.data)
    return (
      <div className={cn(page, 'pt-10')}>
        <FormError error={account.error} />
      </div>
    )
  return <Account key={id} account={account.data} />
}

function Account({ account }: { account: AccountProfile }) {
  const phone = useIsPhone()
  const tabs = account.sections.length ? TABS.filter((t) => t.key === 'all' || account.sections.includes(t.key)) : []
  const [tab, setTab] = useState<Tab>('all')
  const { root, tabRow, pager } = useTabSwipe({
    keys: tabs.map((t) => t.key),
    active: tab,
    still: true,
    onTurn: setTab,
  })
  return (
    <div ref={root} className="flex flex-col pt-4 nav:pt-14">
      {phone && (
        <BarRow pinned>
          <span className="truncate text-[1.0625rem] font-semibold">{account.name}</span>
        </BarRow>
      )}
      <Header account={account} />
      {tabs.length > 0 ? (
        <>
          <div
            ref={tabRow}
            role="group"
            aria-label={`${account.name}’s sections`}
            className="mp-tabs mt-6 grid auto-cols-fr grid-flow-col border-b nav:mx-auto nav:w-full nav:max-w-(--gallery)"
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                aria-label={t.label}
                title={t.label}
                aria-pressed={t.key === tab}
                onClick={() => setTab(t.key)}
                className={cn(
                  'grid h-12 place-items-center border-b-2 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  t.key === tab ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground',
                )}
              >
                <t.icon className="size-6.5" active={t.key === tab} />
              </button>
            ))}
          </div>
          <div ref={pager} className="min-h-[60svh] nav:mx-auto nav:w-full nav:max-w-(--gallery)">
            <Items account={account} tab={tab} />
          </div>
        </>
      ) : (
        <p className={cn(page, 'mt-10 text-center text-muted-foreground')}>
          {account.you ? 'Nothing you’ve added is here yet.' : `Nothing of ${account.name}’s is shared with you yet.`}
        </p>
      )}
    </div>
  )
}

function Header({ account }: { account: AccountProfile }) {
  const ask = useAsk()
  const [noting, setNoting] = useState(false)
  const about = account.you
    ? 'You'
    : account.status === 'connected'
      ? 'Connected'
      : account.mutual.length
        ? `Knows ${bothKnow(account.mutual)}`
        : ''
  return (
    <div className={cn(page, 'flex flex-col gap-4')}>
      <div className="flex items-center gap-5 nav:gap-8">
        <Avatar person={account} variant="display" className="size-22 shrink-0 text-4xl nav:size-36 nav:text-6xl" />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-title text-balance">{account.name}</h1>
          {about && <p className="text-sub text-muted-foreground">{about}</p>}
        </div>
        {account.direct && (
          <div className="self-start">
            <ConnectionOptions userId={account.id} name={account.name} />
          </div>
        )}
      </div>
      {account.circles.length > 0 && (
        <ul aria-label="Circles you’re both in" className="flex flex-wrap gap-1.5">
          {account.circles.map((circle) => (
            <li key={circle.id}>
              <Link
                to={`/circle/${circle.id}`}
                className="flex min-h-8 items-center rounded-full bg-secondary px-3 text-xs font-medium outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11"
              >
                {circle.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {!account.you && account.status !== 'connected' && (
        <div className="flex flex-col gap-2">
          <FormError error={ask.error} />
          {account.status === 'asked' ? (
            <Button variant="secondary" disabled className="self-start">
              Asked
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button disabled={ask.isPending} onClick={() => ask.mutate({ to: account.id })}>
                {account.status === 'asks' ? 'Accept' : 'Connect'}
              </Button>
              {account.status !== 'asks' && (
                <Button variant="secondary" onClick={() => setNoting(true)}>
                  Add a note
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      <AskDialog person={noting ? account : null} onClose={() => setNoting(false)} />
    </div>
  )
}

// Their things as a grid of squares, three across: a photo where there is one, else its title.
function Items({ account, tab }: { account: AccountProfile; tab: Tab }) {
  const items = useAccountItems(account.id, tab === 'all' ? null : tab, true)
  const end = useRef<HTMLDivElement>(null)
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = items
  // The next page comes as the end of this one scrolls into view.
  useEffect(() => {
    const el = end.current
    if (!el || !hasNextPage) return
    const seen = new IntersectionObserver((entries) => {
      if (entries.some((x) => x.isIntersecting) && !isFetchingNextPage) void fetchNextPage()
    })
    seen.observe(el)
    return () => seen.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const list = items.data?.pages.flatMap((p) => p.items) ?? []
  return (
    <>
      <FormError error={items.error} />
      {items.isPending && <Loader label="Loading" className="py-10" />}
      {items.data && list.length === 0 && (
        <p className="py-10 text-center text-muted-foreground">Nothing here you can see.</p>
      )}
      <ul aria-label={TABS.find((t) => t.key === tab)?.label} className="grid grid-cols-3 gap-0.5">
        {list.map((x) => (
          <li key={`${x.kind}:${x.id}`}>
            <Tile item={x} />
          </li>
        ))}
      </ul>
      <div ref={end} className="flex justify-center py-6">
        {isFetchingNextPage && <Loader label="Loading more" now />}
      </div>
    </>
  )
}

function Tile({ item }: { item: FeedItem }) {
  const label = item.title || item.text || { photo: 'Photo', moment: 'Moment', recipe: 'Recipe' }[item.kind as string]
  return (
    <Link
      to={feedLink(item)}
      aria-label={label}
      className="relative block aspect-square overflow-hidden bg-secondary outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      {item.photo && item.photoKind ? (
        <img
          src={refUrl(item.photoKind, item.photo.ref, 'thumb')}
          alt=""
          loading="lazy"
          className="size-full object-cover"
        />
      ) : (
        <span className="flex size-full items-center justify-center p-2 text-center text-sub leading-snug font-medium text-balance">
          <span className="line-clamp-4">{label}</span>
        </span>
      )}
      {item.photo && item.kind !== 'photo' && item.title && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-linear-to-t from-black/60 px-2 pt-4 pb-1.5 text-xs font-medium text-white">
          {item.title}
        </span>
      )}
    </Link>
  )
}
